/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variants = this.data.combinedListing ? this.data.combinedListing.variants : this.data.product.variants;
      this.isProductFromCombinedListing = this.data.combinedListing ? true : false;
      this.variant = this.getSelectedVariant();

      // immediately update the url so the correct loyalty points are shown
      this.updateUrl(null, null);
      this.updateAvailableOptions();
      this.updateSelectedOptionPrice();
      this.updateAvailability();
      this.updateAddToCartButton();

      // Only update price if special price is out of sync
      const customerSpecialPriceEl = this.section.querySelector('[data-customer-special-price]');
      if (customerSpecialPriceEl && this.variant) {
        const currentSpecialPrice = customerSpecialPriceEl.innerHTML.trim();
        const correctSpecialPrice = this.data.formatted[this.variant.id]?.customerSpecialPrice || '';
        if (currentSpecialPrice !== correctSpecialPrice) {
          this.updatePrice();
        }
      }

      this.addEventListener('change', this.handleVariantChange.bind(this));
      document.addEventListener('on:quick-add:opened', this.updateAddToCartButton.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    async handleVariantChange(evt) {
      const pageOrigin = window.location.origin;
      this.variant = null;
      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      let productHandle = null;
      let productDataApi = null;

      if (this.isProductFromCombinedListing) {
        const originalProductId = this.data?.formatted[this.variant.id]?.originalProductId || this.variant?.featured_image?.product_id;
        productHandle = this.data?.formatted[this.variant.id]?.handle || '';
        if (!productHandle) {
          if (originalProductId) {
            productDataApi = await this.fetchVariantProduct(originalProductId);
            productHandle = productDataApi.data.product.handle;
          }
        }
      }

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt, productHandle);
        if (productHandle) {
          await this.fetchAndUpdateContent(`${pageOrigin}/products/${productHandle}`);
        }
        this.updateVariantInput();
      }

      this.updateAvailableOptions();
      this.updateAddToCartButton();
      this.updatePackageVolume();
      this.updatePackageVolume('package-volume-thermobox', 'packageVolumeThermobox');
      this.updateAvailability();
      this.updateTitle();
      this.updateLinks(`${pageOrigin}/products/${productHandle}`);
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          isProductFromCombinedListing: this.isProductFromCombinedListing,
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.getVariantAvailability();
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    updatePackageVolume(cartProperty = 'package-volume', variantProperty = 'packageVolume') {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) {
        return;
      }

      if (!this.productForm.getAttribute(cartProperty)) {
        return;
      }

      if (!this.data.formatted[this.variant.id][variantProperty]) {
        return;
      }

      this.productForm.setAttribute(cartProperty, this.data.formatted[this.variant.id][variantProperty]);
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available, price, compareAtPrice) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      const allVariantsUnavailable = optionEl.closest('[data-is-any-variant-available="false"]');
      if (allVariantsUnavailable) {
        available = true;
      }
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');
        const priceElement = el.querySelector('[data-custom-select-option-price]')

        if (priceElement && (price || compareAtPrice)) {
          const priceHtml = this.getPriceHtml(price, compareAtPrice);

          priceElement.innerHTML = priceHtml;
        }

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          priceElement.innerHTML = '';
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Determines the availability of the current variant, prioritizing the sold out metafield.
     */
    getVariantAvailability() {
      if (!this.variant) return null;
      const formattedVariant = this.data.formatted[this.variant.id];
      if (formattedVariant?.variantSoldOut !== undefined) {
        return formattedVariant.variantSoldOut !== 'true';
      }

      const variants = this.data.combinedListing ? this.data.combinedListing.variants : this.data.product.variants;
      const currentVariantData = variants.find(v => v.id === this.variant.id);

      return currentVariantData?.available ?? false;
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();
      const variantAvailable = this.variant && this.getVariantAvailability();
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
      const availabilityElement = this.section.querySelector('.product-availability');

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (variantAvailable) {
        availabilityElement.classList.add('product-availability--available');
        availabilityElement.classList.remove('product-availability--unavailable');

        availabilityElement.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 21" width="20" height="21" fill="none">
              <path fill="currentColor" d="M10 .734c-5.514 0-10 4.486-10 10s4.486 10 10 10 10-4.486 10-10-4.486-10-10-10Zm0 18.182c-4.512 0-8.182-3.67-8.182-8.182 0-4.511 3.67-8.182 8.182-8.182 4.511 0 8.182 3.67 8.182 8.182 0 4.512-3.67 8.182-8.182 8.182Z"/>
              <path fill="currentColor" d="M13.75 7.199 8.607 12.34 6.25 9.984a.91.91 0 0 0-1.285 1.286l3 3a.91.91 0 0 0 1.286 0l5.784-5.786A.909.909 0 1 0 13.75 7.2Z"/>
          </svg>
          ${window.theme.strings.availability.available}
        `;
      } else {
        availabilityElement.classList.remove('product-availability--available');
        availabilityElement.classList.add('product-availability--unavailable');

        availabilityElement.innerHTML = unavailableStr;
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  variant.available,
                  this.data.formatted[variant.id].price,
                  this.data.formatted[variant.id].compareAtPrice
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            let variantPrice = null;
            let compareAtPrice = null;
            this.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                variantPrice = this.data.formatted[v.id].price;
                compareAtPrice = this.data.formatted[v.id].compareAtPrice;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable, variantPrice, compareAtPrice);
          });
        });
      }
    }

    /**
     * Updates the available options.
     */
    /**
 * Updates the available options.
 */
    updateAvailableOptions() {
      if (!this.data.availableOptions || !this.variant) {
        return;
      }

      const currentOptions = [];
      for (let i = 1; i <= 10; i++) {
        const optionValue = this.variant[`option${i}`];
        if (optionValue && optionValue !== 'Default Title') {
          currentOptions.push(optionValue);
        }
      }

      const optionElements = this.querySelectorAll('[value], [data-value]');

      optionElements.forEach(el => {
        const value = el.getAttribute('value') || el.getAttribute('data-value');
        if (!value) return;

        // Guess which dimension this element belongs to by checking if its value appears in availableOptions
        let optionIndex = -1;
        for (let i = 0; i < 3; i++) { // assuming maximum 3 options (you can extend this to 10 if needed)
          if (this.data.availableOptions.some(opt => opt[i] === value)) {
            optionIndex = i;
            break;
          }
        }
        if (optionIndex === -1) {
          // Value not found in available options
          el.setAttribute('data-disabled', 'true');
          return;
        }

        const isAvailable = this.data.availableOptions.some(optionCombo => {
          // Check that the current selection matches, except at optionIndex where we test different values
          for (let i = 0; i < optionCombo.length; i++) {
            if (i === optionIndex) {
              // This is the option we are changing, so check if it matches the tested value
              if (optionCombo[i] !== value) {
                return false;
              }
            } else {
              // For other options, check if it matches the currently selected option
              if (currentOptions[i] && optionCombo[i] !== currentOptions[i]) {
                return false;
              }
            }
          }
          return true;
        });

        if (isAvailable) {
          el.removeAttribute('data-disabled');
        } else {
          el.setAttribute('data-disabled', 'true');
        }
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_image);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the title.
     */
    updateTitle() {
      const title = this.section.querySelector('.product-title');
      const titleWithLink = title.querySelector('.js-prod-link');

      if (titleWithLink) {
        titleWithLink.innerHTML = this.data.product.title;
      } else {
        title.innerHTML = this.data.product.title;
      }
    }

    /**
     * Updates the title.
     */
    updateLinks(linkUrl) {
      const links = this.section.querySelectorAll('.js-prod-link');

      links.forEach((link) => link.href = linkUrl);
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;
      if (this.variant) {
        const customerSpecialPriceEl = document.querySelector('[data-customer-special-price]');
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        this.updateSelectedOptionPrice();
        const discountPercentage = Math.round((this.variant.compare_at_price - this.variant.price) * 100 / this.variant.compare_at_price);
        const priceWasHtml = this.data.formatted[this.variant.id]?.compareAtPrice
          ? `<s>${this.data.formatted[this.variant.id].compareAtPrice}</s>
            <span class="mr-2">&nbsp;(-${discountPercentage}%)</span>`
          : '';
        // Update current price and original price if on sale.
        if (customerSpecialPriceEl) {
          customerSpecialPriceEl.innerHTML = this.data.formatted[this.variant.id].customerSpecialPrice;
        }
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = priceWasHtml;

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     * @param {string} productHandle - The handle (unique identifier) of the product.
     */
    updateUrl(evt, productHandle) {
      // Only skip if called from a change event and updateUrl is false
      if (this.dataset.updateUrl === 'false') return;
      if (productHandle) {
        window.history.replaceState({}, '', productHandle);
      } else {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
      }
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        const handleInput = form.querySelector('input[name="handle"]');
        if (handleInput) {
          handleInput.value = this.data.product.handle;
        }
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('[data-custom-select-selected-value]').textContent.trim());
        } else {
          selectedOptions.push(selector.querySelector('input:checked').value);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();

      return this.variants.find(
        (v) => {
          const isDefaultTitleInOptions = v.options.some((option) => option === 'Default Title');
          const variantOptions = isDefaultTitleInOptions ? v.options.slice(0, -1) : v.options;

          return variantOptions.every((val, index) => val === selectedOptions[index])
        }
      )
    }

    updateSelectedOptionPrice() {
      const { price, compareAtPrice } = this.data.formatted[this.variant.id];
      const priceElements = this.section.querySelectorAll('[data-custom-select-selected-price]');
      const priceHtml = VariantPicker.getPriceHtml(price, compareAtPrice);

      priceElements.forEach((priceElement) => {
        priceElement.innerHTML = priceHtml;
      })
    }

    static getPriceHtml(price, compareAtPrice) {
      // Parse price strings like "1.090 Kč", "1 090,50 Kč", "1090 Kč"
      function parsePrice(str) {
        if (!str) return 0;
        // Remove all non-digit, non-separator characters
        let cleaned = str.replace(/[^\d.,-]/g, '').replace(/\s/g, '');

        // If both '.' and ',' exist: '.' is thousands, ',' is decimal
        if (cleaned.includes('.') && cleaned.includes(',')) {
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        }
        // If only '.' and matches thousands pattern (e.g. 1.090)
        else if (cleaned.includes('.') && /^\d{1,3}\.\d{3}$/.test(cleaned)) {
          cleaned = cleaned.replace(/\./g, '');
        }
        // If only ',' treat as decimal
        else if (cleaned.includes(',')) {
          cleaned = cleaned.replace(',', '.');
        }
        return parseFloat(cleaned);
      }

      const numericPrice = parsePrice(price);
      const numericCompareAtPrice = parsePrice(compareAtPrice);
      const hasDiscount = compareAtPrice && numericCompareAtPrice > numericPrice;
      const discountPercentage = hasDiscount
        ? Math.round(((numericCompareAtPrice - numericPrice) / numericCompareAtPrice) * 100)
        : null;

      return `
        ${hasDiscount ? `<s>${compareAtPrice}</s>` : ''}
        <strong class="${hasDiscount ? 'color-sale' : ''}">${price}</strong>
        ${discountPercentage ? `<span>(-${discountPercentage}%)</span>` : ''}
      `;
    }

    async fetchAndUpdateContent(url) {
      const sectionSelector = document.querySelector('[data-product-section-id]');
      const section = document.querySelector('.product.js-product');
      const sectionId = sectionSelector?.dataset.productSectionId ?? section?.dataset.section;

      await fetch(`${url}?sections=${sectionId}&add-assets=false&lazy-load=false`)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(response => {
          const parser = new DOMParser();
          const html = parser.parseFromString(response[sectionId], 'text/html');
          const oldSections = document.querySelectorAll('.refreshWithVariant');
          const newSections = html.querySelectorAll('.refreshWithVariant');
          if (this.section.matches('quick-add-drawer')) {
            const productBoxTag = html.querySelector('.product-box-tag');
            this.section.updateBoxTag(productBoxTag?.innerHTML);
          }
          const newSectionsMap = new Map();

          newSections.forEach(newSection => {
            const key = newSection.dataset.refreshWithVariant;
            if (key) {
              newSectionsMap.set(key, newSection);
            }
          });

          oldSections.forEach(oldSection => {
            const key = oldSection.dataset.refreshWithVariant;
            if (key && newSectionsMap.has(key)) {
              oldSection.innerHTML = newSectionsMap.get(key).innerHTML;
            }
          });
        })
        .catch(error => {
          console.error('Fetch error:', error);
        });
      this.data = this.getProductData();
    }

    async fetchVariantProduct(productId) {
      try {
        const response = await fetch(`${window.location.origin}/api/2025-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.sga.settings.storefrontVariantPickerApiKey,
          },
          body: JSON.stringify({
            query: `
                query getProductById {
                  product(id: "gid://shopify/Product/${productId}") {
                    handle
                  }
                }
              `}),
        });

        if (!response.ok) {
          console.error('Network response was not ok', response);
          throw new Error('Network response was not ok');
        }

        const responseData = await response.json();
        return responseData;
      } catch (error) {
        console.error('Fetch GraphQL error:', error);
        throw error;
      }
    };
  }

  customElements.define('variant-picker', VariantPicker);
}
